import onReady from '../utils/onReady';
import domIterator from '../utils/domIterator';
import queryStringParam from '../utils/queryStringParam';
import * as ClassList from '../utils/classList';
import triggerCustomEvent from '../utils/triggerCustomEvent';

/**
 * Boilerplate class demonstrating basic construction.
 * You can expand from here, or find verbose examples in example.js.
 *
 * @param {HTMLElement} container
 * @constructor
 */
class GlobalSearch {

    /**
     * The constructor is fired once the class is instantiated.
     *
     * @param {HTMLElement} container
     */
    constructor(container) {
    	this.isPaginationSearch = false;
        // Run initializing code once the DOM is ready.
        onReady(() => this.init(container));

        // You can put other initializer-code here if it isn't dependent on the DOM in any way.
    }

    removePossibleInjectionThreads(inputString) {
        if (inputString)
        {
            inputString = inputString.length > 150 ? inputString.toLowerCase().substr(0, 150) : inputString.toLowerCase();

            inputString = inputString.toLowerCase().replace(/[<>=;,\.()]+/gi, "");

			inputString = encodeURI(inputString);

            return inputString;
        }
        return "";
    }


    addEvents() {
        this.dom.container.addEventListener("submit", e => {
             e.preventDefault();
             this.handleSubmit();
        });

        this.dom.container.addEventListener("syncSearchText", event => {
            this.dom.input.value = decodeURI(event.detail.searchText);
        });
    }

    handleSubmit() {
        this.query.text = this.dom.input.value;
        this.query.page = 1;

        // If not on Soeg result page, go to the page where searchresults are shown
        if (this.settings.currentPage != "/soeg") {
            window.location = window.location.protocol + "//" + window.location.hostname + "/soeg" + "?q=" + this.removePossibleInjectionThreads(this.query.text);
        } else {
            this.prepareQuery();
        }
    }

    prepareQuery() {
        // If we are not on a subsite, we need to figure out which radio button for site types is checked
        if (!this.isSubSite) {
            this.query.siteType = this.getSiteType();
        }

        let queryString = `?query=${this.removePossibleInjectionThreads( this.query.text )}&page=${this.query.page}&pageSize=10${this.query.siteTypeText}${this.query.siteType}`;

        this.getData(queryString);
    }

    searchFromRedirect() {
        this.query.text = queryStringParam("q");
        let queryString = `?query=${this.removePossibleInjectionThreads( this.query.text)}&page=1&pageSize=10${this.query.siteTypeText}${this.query.siteType}`;

        this.getData(queryString);
    }

    // Sync query text across all global search modules
    syncSearchText() {
        domIterator(this.dom.globalSearchModules, element => {
            triggerCustomEvent(element, "syncSearchText", {searchText: this.removePossibleInjectionThreads( this.query.text )});
        });
    }

    getSiteType() {
        let siteTypeRadios = document.querySelectorAll(".global-search-main__radio input");

        for (let i = 0; i < siteTypeRadios.length; i += 1) {
            if (siteTypeRadios[i].checked) {
                return i;
            }
        }
    }

    getData(value) {
        this.syncSearchText();
        // Visually show user that search is in progress
        ClassList.add(this.dom.resultContainer, "global-search-results--searching");

        // Add spinner
        triggerCustomEvent(this.dom.globalSearchMainSubmitButton, "addSpinner");

        // Add query text to url
        window.history.replaceState({}, "Global søgning", "/soeg?q=" + this.removePossibleInjectionThreads( this.query.text));

        fetch(this.settings.apiUrl + value, {
            method: 'get',
            headers: new Headers({
                'Accept': 'application/json'
            })
        })
        .then(this.handleResponse.bind(this))
        .then(data => this.addContent(data))
        .catch(error => this.handleError(error));
    }

    handleError(error) {
        window.console.log("Catch: " + error);

        this.clearResults();

        let searchResultElement = document.createElement("div"),
            searchResultHtml = `
                    <p>
                        Der skete en fejl i forsøget på at søge. Prøv igen senere.
                    </p>
                `;

        ClassList.add(searchResultElement, "box__outer");
        ClassList.add(searchResultElement, "box__outer--no-shadow");
        searchResultElement.innerHTML = searchResultHtml;

        this.dom.resultContainer.appendChild(searchResultElement);

        this.searchComplete();
    }

    handleResponse(response) {
        let contentType = response.headers.get('content-type');
        if (contentType.includes('application/json')) {
            return this.handleJSONResponse(response);
        } else if (contentType.includes('text/html')) {
            return this.handleTextResponse(response);
        } else {
            throw new Error(`Sorry, content-type ${contentType} not supported`);
        }
    }

    handleJSONResponse(response) {
        return response.json()
            .then(json => {
                if (response.ok) {
                    return json;
                } else {
                    return Promise.reject(Object.assign({}, json, {
                        status: response.status,
                        statusText: response.statusText
                    }));
                }
            });
    }

    handleTextResponse(response) {
        return response.text()
            .then(text => {
                if (response.ok) {
                    return text;
                } else {
                    return Promise.reject({
                        status: response.status,
                        statusText: response.statusText,
                        err: text
                    });
                }
            });
    }

    createTypeAheadListitem(data) {
        // http://wesbos.com/template-strings-html
        let temp = document.createElement('div'),
            listitem,
            tempString = `<li>
                <a href="${data.Link}">
                    ${data.ShowName}
                </a>
            </li>
            `;

        // trick to change the string to a html element to return
        temp.innerHTML = tempString;
        listitem = temp.firstChild;

        return listitem;
    }

    addContent(data) {

        this.searchComplete();

        // If data is present, make list, else show error message
        if (data.NrOfSearchResults === 0) {
            // Show that nothing was found that matches the query

            this.clearResults();

            let searchResultElement = document.createElement("div"),
                searchResultHtml = `
                    <p>
                        Din søgning <b>${this.query.text}</b> matchede ikke noget.
                    </p>
                `;

            ClassList.add(searchResultElement, "box__outer");
            ClassList.add(searchResultElement, "box__outer--no-shadow");
            searchResultElement.innerHTML = searchResultHtml;

            this.dom.resultContainer.appendChild(searchResultElement);

        } else {

            this.clearResults();

            // Loop through each search result
            data.Results.forEach( element => {

                let linkWithRef = element.link + ( (element.link.indexOf("?") == -1) ? "?" : "&") + "ref=" + encodeURIComponent(window.location.href);

                let searchResultElement = document.createElement("div"),
                    searchResultFileType = element.mime == "application/pdf" ? `<p class="pre-text">Filformat: PDF</p>` : "",

                    searchResultHtml = `
                        <a href="${linkWithRef}">
                            <div class="box box--auto-height box--full-width">
                                <div class="box__content">
                                    <div class="global-search-result">
                                        <div class="global-search-result__content">
                                            ${searchResultFileType}
                                            <h4 class="box__headline">
                                                ${element.htmlTitle}
                                            </h4>
                                            <p class="pre-text pre-text--link-color">
                                                ${element.htmlFormattedUrl}
                                            </p>
                                            <p class="box__text box__text--no-margin">
                                                ${element.htmlSnippet}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </a>
                    `;

                ClassList.add(searchResultElement, "box__outer");
                searchResultElement.innerHTML = searchResultHtml;

                this.dom.resultContainer.appendChild(searchResultElement);
            });

			// If we clicked a page in the pagination, add focus to the first search result
			// to make it easier to reach it without having to TAB through the hole page again.
			if (this.isPaginationSearch) {
				this.dom.resultContainer.querySelector("a").focus();
				// reset this again so that every search won't mess with the TAB "order"
				this.isPaginationSearch = false;
			}

            // If CurrentPageNr is 5 or more, we can safely subtract 4
            // let modifiedPageNr = data.CurrentPageNr > 4 ? data.CurrentPageNr - 4 : 1;

            // edit: Since API fails at pagenumber above 10, we don't shift the numbers as of now, and always show from 1 to 10
            let modifiedPageNr = 1;

            // Loop through each available page
            for (let i = 0; i < data.NrOfPages; i += 1) {
                if (i + modifiedPageNr > 10) {
                    break;
                }

				let pageLink = `${i + modifiedPageNr}`;

                let paginationListItem = document.createElement('li');
				ClassList.add(paginationListItem, "global-search-pagination__item");

                let paginationLink = document.createElement('a');
				paginationLink.href = window.location.href;
                paginationLink.dataset.page = i + modifiedPageNr;
				paginationLink.innerHTML = pageLink;

                if (i + modifiedPageNr === data.CurrentPageNr) {
                    ClassList.add(paginationListItem, "global-search-pagination__item--active")
					paginationLink.setAttribute("aria-current", "true");
					paginationLink.setAttribute("aria-label", `Gå til side ${pageLink}, nuværende side`);
				} else {
					paginationLink.setAttribute("aria-label", `Gå til side ${pageLink}`);
				}

                paginationLink.addEventListener("click", (e) => {
                	e.preventDefault();
                    // Set the page for the query string
                    this.query.page = paginationLink.dataset.page;

                    // Visually show user that search is in progress
                    ClassList.add(this.dom.resultContainer, "global-search-results--searching");

					// Tell the search that it is a pagination search
					this.isPaginationSearch = true;

                    // Prepare query string and initiate search
                    this.prepareQuery();
                });

				paginationListItem.appendChild(paginationLink);
                this.dom.paginationContainer.appendChild(paginationListItem);
            }
        }
    }


    searchComplete() {
        //once we reach this point, we know our fetch is complete, and we can remove the spinner from the search button
        triggerCustomEvent(this.dom.globalSearchMainSubmitButton, "fetchComplete");

        // Remove focus from input element, to hide keyboard on mobile devices
        this.dom.input.blur();

        // Remove searching class, if it exists
        if (ClassList.contains(this.dom.resultContainer, "global-search-results--searching")) {
            ClassList.remove(this.dom.resultContainer, "global-search-results--searching");
        }
    }


    clearResults() {
        // Empty the search result container
        this.dom.resultContainer.innerHTML = "";

        // Empty the pagination container
        this.dom.paginationContainer.innerHTML = "";
    }

    /**
     * The actual initialization function, fired once the DOM is ready.
     *
     * @param {HTMLElement} container
     */
    init(container) {

        this.dom = {
            container, // ES6 allows this syntax instead of writing "container: container"
            input: container.querySelector("input"),
            resultContainer: document.querySelector(".global-search-results"),
            paginationContainer: document.querySelector(".global-search-pagination"),
            globalSearchModules: document.querySelectorAll(".global-search"),
            globalSearchMainSubmitButton: document.querySelector("#global-search-main button")
        };

        // Put your other DOM-specific initializations here, and get your class moving!

        // http://lejerbo-local.dis-play.dk/api/gcs/search?query=WhateverToSearchForString&page=1&pageSize=10&siteType=0

        this.isSubSite = this.dom.container.dataset.issubsite == "True" ? true : false;

        this.settings = {
            apiUrl: window.location.protocol + "//" + window.location.hostname + "/api/gcs/" + (this.isSubSite ? "subsitesearch" : "search"),
            currentPage: window.location.pathname ? window.location.pathname : null
        };

        this.query = {
            text: "",
            page: 1,
            pageSize: 10,
            siteType: (this.isSubSite ? "": 0),
            siteTypeText: (this.isSubSite ? "" : "&siteType=")
        };

        this.addEvents();


        // If this module is the main global search module AND there is a query string, then call handleSubmit on load
        if (ClassList.contains(this.dom.container, "global-search-main") && queryStringParam("q")) {
            this.searchFromRedirect();
        }
    }

}


export default GlobalSearch;
